import React, { useEffect, useState } from 'react';
import { Chart } from '../../../../HealthData/Enums/Chart.enum';
import useHealthData, {
	IHealthData,
} from '../../../../HealthData/Hooks/useHealthData';
import { HealthDataFilterFormValues } from '../../../../HealthData/Components/Filter/HealthDataFilterForm';
import HealthDataWrapper from '../../../../HealthData/Components/HealthDataWrapper';
import HealthDataChart from '../../../../HealthData/Components/Charts/HealthDataChart';
import {
	QUESTIONNAIRE_ENTRY_CODE,
	QUESTIONNAIRE_REGULAR_CODE,
} from '../../../constants';
import useTranslation from '../../../../../Hooks/useTranslation';
import { useEffectAsync } from '../../../../../helpers';
import { CHARTS_DATASET } from '../../../../HealthData/Utils/chartsDataset';
import { QuestionnairesResultsReStructureScheme } from '../../../../Questionnaire/interfaces';
import { getMomentDate } from '../../../../../Utils/dates.util';
import { HealthDataType } from '../../../../HealthData/Enums/HealthDataType.enum';
import moment from 'moment';
import { HealthData } from '../../../../HealthData/Enums/HealthData.enum';
import QuestionnairesResults from '../../../../Questionnaire/Components/QuestionnaireResults/QuestionnairesResults';
import useMothersWeightBeforePregnancy from '../hooks/useMothersWeightBeforePregnancy';
import loading from '../../../../../Components/Loading';
import Loading from '../../../../../Components/Loading';

interface HealthDataKeys {
	bloodPressure: string[];
	weight: string[];
}

interface HealthDataDataset {
	bloodPressure: IHealthData[];
	weight: IHealthData[];
}

const datasetDefaults = {
	bloodPressure: [],
	weight: [],
};
interface Props {
	patientId: string;
}

const Monitoring: React.FC<Props> = ({ patientId }) => {
	const [filter, setFilter] = useState<HealthDataFilterFormValues>();
	const { filterKeys, filterHealthDataByKeys, getHealthData } = useHealthData();
	const { t } = useTranslation();
	const [showDataset, setShowDataset] =
		useState<HealthDataKeys>(datasetDefaults);
	const [dataset, setDataset] = useState<HealthDataDataset>(datasetDefaults);
	const [isLoading, setIsLoading] = useState(true);
	const { getMothersWeightBeforePregnancy } = useMothersWeightBeforePregnancy();

	useEffect(() => {
		if (filter) {
			setShowDataset({
				bloodPressure: filterKeys(
					filter.keys,
					CHARTS_DATASET[Chart.BLOOD_PRESSURE_SYS_DIA].keys,
				),
				weight: filterKeys(filter.keys, CHARTS_DATASET[Chart.WEIGHT].keys),
			});
		}
	}, [filter]);

	// @ts-ignore
	useEffectAsync(async () => {
		if (filter) {
			setIsLoading(true);
			const data: IHealthData[] = await getHealthData(
				patientId,
				filter.view,
				{
					from: getMomentDate(filter.from),
					to: getMomentDate(filter.to),
				},
				filter.source,
				filter.keys,
				filter.type,
			);

			const weightData = data.find(({ key }) => key === 'weight');
			const ghostData: any = [];
			if (weightData) {
				for (const item of weightData.data || []) {
					ghostData.push({
						measure_name: item.measure_name,
						scope: item.scope,
						token: item.token,
						userId: item.userId,
						value: await getMothersWeightBeforePregnancy(patientId),
						time: item.time,
						source: item.source,
					});
				}
			}
			const mothersWeightBeforePregnancy: IHealthData = {
				data: ghostData,
				description:
					t(
						'component.meddiBabyCzech.monitoring.mothersWeightBeforePregnancy',
					) ?? '',
				key: 'mothersHealth.weighBeforePregnancy',
				unit: 'kg',
			};

			data.push(mothersWeightBeforePregnancy);

			if (data) {
				const sortedData: any[] = [];
				data.forEach((item: any) => {
					sortedData.push({
						description: item.description,
						key: item.key,
						unit: item.unit,
						data: item.data.sort(
							(a: any, b: any) => a.time.format('x') - b.time.format('x'),
						),
					});
				});

				const filterData = {
					bloodPressure: filterHealthDataByKeys(
						sortedData,
						CHARTS_DATASET[Chart.BLOOD_PRESSURE].keys,
					),
					weight: filterHealthDataByKeys(
						sortedData,
						CHARTS_DATASET[Chart.WEIGHT].keys,
					),
				};
				// @ts-ignore
				setDataset(filterData);
			}

			setIsLoading(false);
		}
	}, [filter]);

	useEffect(() => {
		const filterValues: HealthDataFilterFormValues = {
			from: moment().subtract(1, 'year').startOf('day'),
			to: moment().add(1, 'day').startOf('day'),
			view: 'week',
			type: HealthDataType.RAW,
			source: ['questionnaire'],
			keys: [
				HealthData.WEIGHT,
				HealthData.BLOOD_PRESSURE_SYSTOLIC,
				HealthData.BLOOD_PRESSURE_DIASTOLIC,
			],
		};

		setFilter(filterValues);
	}, []);

	const questionnairesResultsStructure: QuestionnairesResultsReStructureScheme[] =
		[
			{
				title: t('base.gestationalWeek') ?? '',
				key: 'weekOfPregnancy',
				contains: [
					'currentPregnancy.weekOfPregnancy',
					'mothersHealth.weekOfPregnancy',
				],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.actualWeight') ?? '',
				key: 'mothersActualWeight',
				contains: ['mothersHealth.actualWeight', 'mothersActualWeight'],
				unit: 'kg',
			},
			{
				title:
					t('component.meddiBabyCzech.monitoring.weightGainDuringPregnancy') ??
					'',
				key: 'mothersWeightGainDuringPregnancy',
				contains: [
					'additionalWeightInPregnancy',
					'mothersHealth.additionalWeightInPregnancy',
				],
				unit: 'kg',
			},
			{
				title:
					t(
						'component.meddiBabyCzech.monitoring.weightGainSincePreviousMeasurement',
					) ?? '',
				key: 'mothersWeightGainSincePreviousMeasurement',
				contains: ['additionalWeightSinceLastMeasure'],
				unit: 'kg',
			},
			{
				title:
					t('component.meddiBabyCzech.monitoring.systolicBloodPressure') ?? '',
				key: 'mothersSystolicBloodPressure',
				contains: [
					'mothersHealth.systolicBloodPressure',
					'mothersSystolicBloodPressure',
				],
				unit: 'mmHg',
			},
			{
				title:
					t('component.meddiBabyCzech.monitoring.diastolicBloodPressure') ?? '',
				key: 'mothersDiastolicBloodPressure',
				contains: [
					'mothersHealth.diastolicBloodPressure',
					'mothersDiastolicBloodPressure',
				],
				unit: 'mmHg',
			},
			{
				title: t('component.meddiBabyCzech.monitoring.swellings') ?? '',
				key: 'mothersSwelling',
				contains: ['mothersHealth.edemas', 'mothersSwelling'],
				emptyRules: {
					placeholder: 'base.absent',
					key: 'mothersHasSwelling',
					value: 'NE',
				},
			},
			{
				title: t('component.meddiBabyCzech.monitoring.urineGlucose') ?? '',
				key: 'mothersGlucose',
				contains: ['mothersHealth.urine.glucose.label', 'mothersGlucose'],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.urineProtein') ?? '',
				key: 'mothersProtein',
				contains: ['mothersHealth.urine.protein.label', 'mothersProtein'],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.ctg') ?? '',
				key: 'mothersProtein',
				contains: ['mothersHealth.ctg.label'],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.fetalMovements') ?? '',
				key: 'mothersFetalMovements',
				contains: ['mothersFetalMovements'],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.vaginalBleeding') ?? '',
				key: 'mothersVaginalBleeding',
				contains: ['mothersVaginalBleedingPower'],
				emptyRules: {
					placeholder: 'NE',
					key: 'mothersVaginalBleeding',
					value: 'NE',
				},
			},
			{
				title:
					t('component.meddiBabyCzech.monitoring.drainageOfAmnioticFluid') ??
					'',
				key: 'mothersDrainageOfAmnioticFluid',
				contains: ['mothersDrainageOfAmnioticFluid'],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.contractionsPain') ?? '',
				key: 'mothersContractions',
				contains: ['mothersContractionsPower'],
				emptyRules: {
					placeholder: 'NE',
					key: 'mothersContractions',
					value: 'NE',
				},
			},
			{
				title:
					t('component.meddiBabyCzech.monitoring.elevatedTemperature') ?? '',
				key: 'mothersElevatedTemperature',
				contains: ['mothersElevatedTemperature'],
			},
			{
				title: t('component.meddiBabyCzech.monitoring.otherTroubles') ?? '',
				key: 'mothersOtherTroubles',
				contains: ['mothersOtherTroubles'],
				emptyRules: {
					placeholder: 'NE',
					key: 'mothersHasOtherTroubles',
					value: 'NE',
				},
			},
		];

	if (isLoading) {
		return <Loading />;
	}

	return (
		<HealthDataWrapper>
			<div className='overflow-auto'>
				<QuestionnairesResults
					patientId={patientId}
					tableName='component.meddiBabyCzech.mothersHealth'
					questionnairesCodes={[
						QUESTIONNAIRE_ENTRY_CODE,
						QUESTIONNAIRE_REGULAR_CODE,
						'meddi_baby_czech_patient',
					]}
					structure={questionnairesResultsStructure}
					roundValuesTo={1}
				/>
				{showDataset.weight.length > 0 && filter && (
					<HealthDataChart
						patientId={patientId}
						filter={filter}
						chartType={Chart.WEIGHT}
						healthData={dataset.weight}
						isLoading={isLoading}
					/>
				)}
				{showDataset.bloodPressure.length > 0 && filter && (
					<HealthDataChart
						patientId={patientId}
						filter={filter}
						chartType={Chart.BLOOD_PRESSURE_SYS_DIA}
						healthData={dataset.bloodPressure}
						isLoading={isLoading}
					/>
				)}
			</div>
		</HealthDataWrapper>
	);
};

export default Monitoring;
